/**
 * DD Merchant Product — Web Component
 *
 * Buy box for real single products listed in Google Merchant Center.
 * Prices are pre-rendered by Liquid from the variant itself, so nothing
 * here computes money. Picking a variant writes ?variant=<id> to the URL
 * so every variant has its own shareable landing page.
 */
class DDMerchantBox extends HTMLElement {
  connectedCallback() {
    // dd-buy-box.js moves the column on mobile, which re-fires this callback.
    if (this._initialized) return;
    this._initialized = true;

    this._variantId = this.dataset.variantId;
    this._btn       = this.querySelector('.dbb-atc-btn');
    this._atcLabel  = this._btn ? this._btn.textContent.trim() : 'Add to Cart';

    this.querySelectorAll('.dbb-size-item[data-variant-id]').forEach((item) => {
      item.querySelector('.dbb-size-btn').addEventListener('click', () => this._selectVariant(item));
    });
    if (this._btn) this._btn.addEventListener('click', () => this._handleAtc());
  }

  _selectVariant(item) {
    this._variantId = item.dataset.variantId;

    this.querySelectorAll('.dbb-size-item[data-variant-id]').forEach((el) => {
      const selected = el === item;
      el.classList.toggle('is-selected', selected);
      el.querySelector('.dbb-size-btn').setAttribute('aria-checked', selected);
    });

    const label = this.querySelector('[data-variant-label]');
    if (label) label.textContent = item.dataset.variantTitle;

    const price   = this.querySelector('[data-price-display]');
    const compare = this.querySelector('[data-compare-display]');
    if (price) price.textContent = item.dataset.price;
    if (compare) {
      compare.textContent = item.dataset.compare;
      compare.hidden = !item.dataset.compare;
    }

    const available = item.dataset.available === 'true';
    if (this._btn) {
      this._btn.disabled    = !available;
      this._btn.textContent = available ? 'Add to Cart' : 'Sold Out';
      this._atcLabel        = this._btn.textContent;
    }

    // Keep ad-tracking params (gclid, utm_*) intact; only swap the variant.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this._variantId);
    window.history.replaceState({}, '', url);
  }

  async _handleAtc() {
    const errorEl = this.querySelector('.dbb-error');
    if (!this._btn || this._btn.disabled) return;

    this._btn.classList.add('is-loading');
    this._btn.disabled = true;
    if (errorEl) errorEl.classList.remove('is-visible');

    try {
      // rebuy-cart-redirect.js opens the Rebuy cart on any successful /cart/add.
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items: [{ id: parseInt(this._variantId, 10), quantity: 1 }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart.');

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
      this._btn.textContent = 'Added to Cart!';
      setTimeout(() => { this._btn.textContent = this._atcLabel; }, 2200);
    } catch (err) {
      console.error('[DD-Merchant-Product]', err);
      if (errorEl) {
        errorEl.textContent = err.message || 'Something went wrong. Please try again.';
        errorEl.classList.add('is-visible');
      }
    } finally {
      this._btn.classList.remove('is-loading');
      this._btn.disabled = false;
    }
  }
}

if (!customElements.get('dd-merchant-box')) {
  customElements.define('dd-merchant-box', DDMerchantBox);
}
